import { useCallback, useEffect, useRef, useState } from 'react'
import { interviewService, ApiError } from '@/services'
import type { GapStatus, Message } from '@/types/api'
import { waitAtMost } from './fillGapsSessionHelpers'

/** Longest a restart waits for the previous session on that gap to end. */
export const END_WAIT_CAP_MS = 5000

interface OwnedSession {
  startupId: string
  sessionId: string
}

export interface UseGapSessionsOptions {
  startupId: string | null
  /** Rows currently expanded on the page. */
  expandedGapIds: string[]
  /** Status per gap with local overrides already applied. */
  statusById: Record<string, GapStatus>
  /** Receives a new session's opening message for its gap. */
  onInitialMessage: (gapId: string, message: Message) => void
  /** Fires when a row collapses and its session is given up. */
  onRelease: (gapId: string) => void
}

export interface UseGapSessionsResult {
  /** Last session-start failure; cleared on retry or once nothing is failing. */
  sessionError: string | null
  /** Re-attempt every failed start whose row is still expanded. */
  retrySession: () => void
  /** Resolve the gap's session, starting one if needed (null on failure). */
  ensureSession: (gapId: string) => Promise<string | null>
}

/** 409 GAP_NOT_AVAILABLE: another active session holds it, or it closed. */
export const GAP_UNAVAILABLE_MESSAGE =
  'Someone else is answering this gap right now, or it was just resolved. Try again in a moment.'

function startErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return GAP_UNAVAILABLE_MESSAGE
  return err instanceof Error ? err.message : 'Could not start a conversation.'
}

/**
 * Workable rows can take a session. For `in_progress` the backend decides:
 * it hands over a stale hold and answers 409 when an active session has it.
 */
function isStartable(status: GapStatus | undefined): boolean {
  return status === 'open' || status === 'in_progress'
}

/**
 * Lock-on-expand interview sessions for Fill Gaps. Expanding a row starts a
 * session targeting exactly that gap; collapsing it ends the session. All
 * sessions end on startup switch, unmount and (best-effort) page hide.
 */
export function useGapSessions(
  options: UseGapSessionsOptions
): UseGapSessionsResult {
  const { startupId, expandedGapIds, statusById } = options
  const [sessionError, setSessionError] = useState<string | null>(null)

  const optionsRef = useRef(options)
  optionsRef.current = options
  // Refs are the synchronous source of truth: cleanup paths run outside
  // React's render cycle, and nothing here needs to re-render.
  const ownedRef = useRef(new Map<string, OwnedSession>())
  const pendingRef = useRef(new Map<string, Promise<string | null>>())
  // In-flight endSession per gap. A quick collapse → re-expand must not ask
  // for the gap before the old session has let go of it.
  const endingRef = useRef(new Map<string, Promise<void>>())
  const failedRef = useRef(new Set<string>())
  const prevExpandedRef = useRef<string[]>([])
  // Bumped whenever everything is ended so in-flight starts discard.
  const epochRef = useRef(0)

  const stillWanted = useCallback(
    (gapId: string, epoch: number) =>
      epoch === epochRef.current &&
      optionsRef.current.expandedGapIds.includes(gapId),
    []
  )

  const endQuietly = useCallback((gapId: string, owned: OwnedSession) => {
    const ending: Promise<void> = interviewService
      .endSession(owned.startupId, owned.sessionId)
      .catch(() => undefined)
      .finally(() => {
        if (endingRef.current.get(gapId) === ending) endingRef.current.delete(gapId)
      })
    endingRef.current.set(gapId, ending)
  }, [])

  const startSession = useCallback(
    async (sid: string, gapId: string): Promise<string | null> => {
      const epoch = epochRef.current
      try {
        // If the end outlasts the cap and the hold remains, the create 409s
        // and the banner offers Retry.
        await waitAtMost(endingRef.current.get(gapId), END_WAIT_CAP_MS)
        if (!stillWanted(gapId, epoch)) return null
        const res = await interviewService.createSession(sid, { gapIds: [gapId] })
        const sessionId = res.sessionId ?? res.session?.id ?? null
        if (!sessionId) return null
        if (!stillWanted(gapId, epoch)) {
          endQuietly(gapId, { startupId: sid, sessionId })
          return null
        }
        ownedRef.current.set(gapId, { startupId: sid, sessionId })
        if (failedRef.current.size === 0) setSessionError(null)
        if (res.initialMessage) {
          optionsRef.current.onInitialMessage(gapId, res.initialMessage)
        }
        return sessionId
      } catch (err) {
        if (!stillWanted(gapId, epoch)) return null
        failedRef.current.add(gapId)
        if (!(err instanceof ApiError && err.status === 401)) {
          setSessionError(startErrorMessage(err))
        }
        return null
      }
    },
    [stillWanted, endQuietly]
  )

  const ensureSession = useCallback(
    (gapId: string): Promise<string | null> => {
      const owned = ownedRef.current.get(gapId)
      if (owned) return Promise.resolve(owned.sessionId)
      const pending = pendingRef.current.get(gapId)
      if (pending) return pending
      if (!startupId) return Promise.resolve(null)
      failedRef.current.delete(gapId)
      const promise: Promise<string | null> = startSession(startupId, gapId).finally(
        () => {
          if (pendingRef.current.get(gapId) === promise) {
            pendingRef.current.delete(gapId)
          }
        }
      )
      pendingRef.current.set(gapId, promise)
      return promise
    },
    [startupId, startSession]
  )

  const release = useCallback(
    (gapId: string) => {
      const owned = ownedRef.current.get(gapId)
      if (owned) {
        endQuietly(gapId, owned)
        ownedRef.current.delete(gapId)
      }
      if (failedRef.current.delete(gapId) && failedRef.current.size === 0) {
        setSessionError(null)
      }
      optionsRef.current.onRelease(gapId)
    },
    [endQuietly]
  )

  const endAll = useCallback(() => {
    epochRef.current += 1
    ownedRef.current.forEach((owned, gapId) => endQuietly(gapId, owned))
    ownedRef.current.clear()
    pendingRef.current.clear()
    failedRef.current.clear()
    setSessionError(null)
  }, [endQuietly])

  // Give up the session of every row that collapsed since last time.
  useEffect(() => {
    const open = new Set(expandedGapIds)
    for (const gapId of prevExpandedRef.current) {
      if (!open.has(gapId)) release(gapId)
    }
    prevExpandedRef.current = expandedGapIds
  }, [expandedGapIds, release])

  // Start a session for each expanded row that can take one. Failed rows
  // wait for an explicit retry so a refetch can't loop on a bad create.
  useEffect(() => {
    for (const gapId of expandedGapIds) {
      if (failedRef.current.has(gapId)) continue
      if (isStartable(statusById[gapId])) void ensureSession(gapId)
    }
  }, [expandedGapIds, statusById, ensureSession])

  // End everything on startup switch and unmount; pagehide is best-effort
  // (the browser may drop the request as the page unloads).
  useEffect(() => {
    window.addEventListener('pagehide', endAll)
    return () => {
      window.removeEventListener('pagehide', endAll)
      endAll()
    }
  }, [startupId, endAll])

  const retrySession = useCallback(() => {
    const failed = [...failedRef.current]
    failedRef.current.clear()
    setSessionError(null)
    for (const gapId of failed) {
      if (optionsRef.current.expandedGapIds.includes(gapId)) {
        void ensureSession(gapId)
      }
    }
  }, [ensureSession])

  return { sessionError, retrySession, ensureSession }
}
